import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  FormFeedback,
  Alert,
  Spinner,
  InputGroup,
  InputGroupText
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { Link } from "react-router-dom"
import withRouter from "components/Common/withRouter"
import logo from "assets/images/light-logo-coldblock.png"
import { clearSuccess, resetUser, sendResetCodeRequest, verifyResetCodeRequest } from "store/actions"
import PortalSupportNotice from "components/Common/PortalSupportNotice"

const StepIndicator = ({ step }) => (
  <div className="d-flex align-items-center justify-content-center gap-3 mb-2 mt-4">
    {[1, 2, 3].map(n => (
      <div key={n} className={`d-flex align-items-center`}>
        <div
          className={`rounded-circle d-inline-flex align-items-center justify-content-center`}
          style={{
            width: 34, height: 34,
            background: step >= n ? "#06427a" : "#e9ecef",
            color: step >= n ? "#fff" : "#6c757d",
            fontWeight: 600
          }}
        >
          {n}
        </div>
        {n !== 3 && <div style={{ width: 40, height: 2, background: step > n ? "#06427a" : "#e9ecef", margin: "0 8px" }} />}
      </div>
    ))}
  </div>
)

const ResetPassword = (props) => {
  document.title = "Reset Password - ColdBlock Technologies Inc.";
  const dispatch = useDispatch();

  const {
    stage,
    loading,
    message,
    resetPassError,
    email,
  } = useSelector((state) => state.ForgetPassword || {})
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [counter, setCounter] = useState(0)

  const formEmail = useFormik({
    enableReinitialize: true,
    initialValues: { email: email || "" },
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email").required("Email is required"),
    }),
    onSubmit: (values) => {
      dispatch(sendResetCodeRequest(formEmail.values.email.trim()));
      setCounter(30);
    },
  });

  const formCode = useFormik({
    initialValues: { code: "" },
    validationSchema: Yup.object({
      code: Yup.string()
        .length(6, "Code must be 6 digits")
        .required("Enter the code"),
    }),
    onSubmit: (values) => {
      const email = formEmail.values.email.trim();
      const code = values.code.trim();
      dispatch(verifyResetCodeRequest(email, code));
    },
  });

  const formDetails = useFormik({
    enableReinitialize: true,
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .required("Password is required")
        .min(8, "At least 8 characters")
        .matches(/\d/, "Must include at least 1 number")
        .matches(/[A-Z]/, "Must include at least 1 uppercase letter")
        .matches(/[a-z]/, "Must include at least 1 lowercase letter")
        .matches(/[!@#$%^&*()[\]{}:;<>,.?/~_+\-=|\\]/, "Must include at least 1 special character")
        .matches(/^\S+$/, "No spaces allowed"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Confirm your password"),
    }),
    onSubmit: (values) => {
      const email = formEmail.values.email.trim();
      const code = (formCode.values.code || "").trim();
      const newPassword = values.password;
      dispatch(resetUser(email, code, newPassword, props.router.navigate));
    },
  });

  useEffect(() => {
    if (counter <= 0) return;
    const t = setTimeout(() => setCounter(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [counter]);

  useEffect(() => {
    if (message || resetPassError) {
      const timer = setTimeout(() => {
        dispatch(clearSuccess())
      }, 7000)
      return () => clearTimeout(timer)
    }
  }, [message, resetPassError, dispatch])

  const Brand = (
    <div className="text-center">
      <Link to="/" className="logo-light-element d-inline-block">
        <img src={logo} alt="" height="65" />
      </Link>
    </div>
  );

  const Header = (
    <div className="bg-primary-subtle">
      <Row className="d-flex align-items-center justify-content-between">
        <Col className="col-6">
          <div className="text-primary p-4">
            <h5 className="text-primary white-txt">Reset your password</h5>
            {/* <p className="white-txt">3 quick steps to get started.</p> */}
            {/* <p className="white-txt">It’s fast and easy.</p> */}
          </div>
        </Col>
        <Col className="col-6">
          {Brand}
        </Col>
      </Row>
    </div>
  );

  const Banner = (
    <>
      {message && <Alert color="success" className="mb-3">{message} <strong>{email}</strong>.</Alert>}
      {resetPassError && <Alert color="danger" className="mb-3">
        {typeof resetPassError === "string" ? resetPassError : resetPassError?.message || "Something went wrong"}
      </Alert>}
    </>
  );

  return (
    <React.Fragment>
      <div className="account-pages vh-100 d-flex justify-content-center align-items-center">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                {Header}
                <CardBody className="pt-3">
                  <StepIndicator step={Number(stage) || 1} />
                  <div className="p-2">
                    {stage === 1 && (
                      <Form
                        className="form-horizontal"
                        onSubmit={(e) => { e.preventDefault(); formEmail.handleSubmit(); }}
                      >
                        {Banner}
                        <div className="mb-3">
                          <Label className="form-label">Email</Label>
                          <Input
                            name="email"
                            type="email"
                            placeholder="you@example.com"
                            value={formEmail.values.email}
                            onChange={formEmail.handleChange}
                            onBlur={formEmail.handleBlur}
                            invalid={!!(formEmail.touched.email && formEmail.errors.email)}
                          />
                          {formEmail.touched.email && formEmail.errors.email && (
                            <FormFeedback type="invalid">{formEmail.errors.email}</FormFeedback>
                          )}
                        </div>
                        <div className="mt-3 d-grid">
                          <button className="btn btn-primary primary-btn-bg" type="submit" disabled={loading}>
                            {loading ? <Spinner size="sm" /> : "Send verification code"}
                          </button>
                        </div>
                        <div className="mt-3 text-center">
                          <Link to="/signin" className="btn btn-link p-0 align-baseline">Sign in</Link>
                        </div>
                      </Form>
                    )}

                    {stage === 2 && (
                      <Form
                        className="form-horizontal"
                        onSubmit={(e) => { e.preventDefault(); formCode.handleSubmit(); }}
                      >
                        {Banner}
                        <div className="mb-3">
                          <Label className="form-label">Verification code</Label>
                          <Input
                            name="code"
                            type="text"
                            placeholder="Enter code"
                            value={formCode.values.code}
                            maxLength={6}
                            onChange={formCode.handleChange}
                            onBlur={formCode.handleBlur}
                            invalid={!!(formCode.touched.code && formCode.errors.code)}
                          />
                          {formCode.touched.code && formCode.errors.code && (
                            <FormFeedback type="invalid">{formCode.errors.code}</FormFeedback>
                          )}
                        </div>
                        <div className="mt-3 d-grid">
                          <button
                            className="btn btn-primary primary-btn-bg"
                            type="submit"
                            disabled={loading || !formCode.isValid}
                          >
                            {loading ? <Spinner size="sm" /> : "Verify code"}
                          </button>
                        </div>
                        <div className="mt-3 text-center">
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            disabled={loading || counter > 0}
                            onClick={() => {
                              dispatch(sendResetCodeRequest(email));
                              setCounter(30);
                            }}
                          >
                            {counter > 0 ? `Resend in ${counter}s` : "Resend code"}
                          </button>
                        </div>
                      </Form>
                    )}

                    {stage === 3 && (
                      <Form
                        className="form-horizontal"
                        onSubmit={(e) => { e.preventDefault(); formDetails.handleSubmit(); }}
                      >
                        {Banner}
                        <div className="mb-3">
                          <Label className="form-label">Email</Label>
                          <Input
                            name="email"
                            type="email"
                            disabled
                            placeholder="you@example.com"
                            value={formEmail.values.email}
                            onChange={formEmail.handleChange}
                            onBlur={formEmail.handleBlur}
                            invalid={!!(formEmail.touched.email && formEmail.errors.email)}
                          />
                          {formEmail.touched.email && formEmail.errors.email && (
                            <FormFeedback type="invalid">{formEmail.errors.email}</FormFeedback>
                          )}
                        </div>

                        <div className="mb-3">
                          <Label className="form-label">Password</Label>
                          <InputGroup>
                            <Input
                              name="password"
                              type={showPassword ? "text" : "password"}
                              placeholder="Create a password"
                              value={formDetails.values.password}
                              onChange={formDetails.handleChange}
                              onBlur={formDetails.handleBlur}
                              autoComplete="new-password"
                              invalid={!!(formDetails.touched.password && formDetails.errors.password)}
                            />
                            <InputGroupText
                              role="button"
                              aria-label={showPassword ? "Hide password" : "Show password"}
                              onClick={() => setShowPassword(v => !v)}
                              onMouseDown={(e) => e.preventDefault()}
                              className="bg-transparent"
                              style={{ cursor: "pointer" }}
                            >
                              <i className={showPassword ? "mdi mdi-eye-off" : "mdi mdi-eye"} />
                            </InputGroupText>
                          </InputGroup>
                          {formDetails.touched.password && formDetails.errors.password && (
                            <FormFeedback type="invalid" style={{ display: "block" }}>
                              {formDetails.errors.password}
                            </FormFeedback>
                          )}
                        </div>

                        <div className="mb-3">
                          <Label className="form-label">Confirm password</Label>
                          <InputGroup>
                            <Input
                              name="confirmPassword"
                              type={showConfirm ? "text" : "password"}
                              placeholder="Re-enter password"
                              value={formDetails.values.confirmPassword}
                              onChange={formDetails.handleChange}
                              onBlur={formDetails.handleBlur}
                              autoComplete="new-password"
                              invalid={!!(formDetails.touched.confirmPassword && formDetails.errors.confirmPassword)}
                            />
                            <InputGroupText
                              role="button"
                              aria-label={showConfirm ? "Hide password" : "Show password"}
                              onClick={() => setShowConfirm(v => !v)}
                              onMouseDown={(e) => e.preventDefault()}
                              className="bg-transparent"
                              style={{ cursor: "pointer" }}
                            >
                              <i className={showConfirm ? "mdi mdi-eye-off" : "mdi mdi-eye"} />
                            </InputGroupText>
                          </InputGroup>
                          {formDetails.touched.confirmPassword && formDetails.errors.confirmPassword && (
                            <FormFeedback type="invalid" style={{ display: "block" }}>
                              {formDetails.errors.confirmPassword}
                            </FormFeedback>
                          )}
                        </div>

                        <div className="mt-3 d-grid">
                          <button
                            className="btn btn-primary primary-btn-bg"
                            type="submit"
                            disabled={loading || !formDetails.isValid}
                          >
                            {loading ? <Spinner size="sm" /> : "Reset Password"}
                          </button>
                        </div>
                      </Form>
                    )}
                    <div className="mt-3 text-center">
                      <PortalSupportNotice />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(ResetPassword);

ResetPassword.propTypes = {
  history: PropTypes.object,
}